import * as SwitchPrimitives from '@radix-ui/react-switch';
import { motion, useMotionValue, animate } from 'framer-motion';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/Domain/utils/cn';
import { springs } from '@/Domain/utils/springs';

const TRACK_WIDTH = 34;
const TRACK_HEIGHT = 20;
const THUMB_SIZE = 16;
const THUMB_OFFSET = 2;
const THUMB_TRAVEL = TRACK_WIDTH - THUMB_SIZE - THUMB_OFFSET * 2;
const PILL_EXTEND = 2;
const PRESS_EXTEND = 4;
const PRESS_SHRINK = 4;
const DRAG_DEAD_ZONE = 2;

type SwitchRootProps = ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>;

export type SwitchProps = SwitchRootProps;

const Switch = forwardRef<ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, checked, defaultChecked, onCheckedChange, disabled, onClick, ...props }, ref) => {
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
    const currentChecked = isControlled ? !!checked : internalChecked;

    const hasMounted = useRef(false);
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);

    const dragging = useRef(false);
    const didDrag = useRef(false);
    const pointerStart = useRef<{ clientX: number; originX: number } | null>(null);

    const motionX = useMotionValue(currentChecked ? THUMB_OFFSET + THUMB_TRAVEL : THUMB_OFFSET);

    useEffect(() => {
      hasMounted.current = true;
    }, []);

    const thumbWidth = pressed
      ? THUMB_SIZE + PRESS_EXTEND
      : hovered
        ? THUMB_SIZE + PILL_EXTEND
        : THUMB_SIZE;
    const thumbHeight = pressed ? THUMB_SIZE - PRESS_SHRINK : THUMB_SIZE;
    const thumbY = pressed ? THUMB_OFFSET + PRESS_SHRINK / 2 : THUMB_OFFSET;
    const extraWidth = thumbWidth - THUMB_SIZE;
    const thumbX = currentChecked ? THUMB_OFFSET + THUMB_TRAVEL - extraWidth : THUMB_OFFSET;

    useEffect(() => {
      if (dragging.current) return;
      if (!hasMounted.current) {
        motionX.set(thumbX);
      } else {
        animate(motionX, thumbX, springs.moderate);
      }
    }, [thumbX, motionX]);

    const emitChange = useCallback(
      (next: boolean) => {
        if (!isControlled) setInternalChecked(next);
        onCheckedChange?.(next);
      },
      [isControlled, onCheckedChange],
    );

    const handlePointerDown = useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        setPressed(true);
        dragging.current = false;
        didDrag.current = false;
        pointerStart.current = {
          clientX: e.clientX,
          originX: motionX.get(),
        };
        const el = e.currentTarget as HTMLElement;
        if (typeof el.setPointerCapture === 'function') {
          try {
            el.setPointerCapture(e.pointerId);
          } catch {
            /* ignore — jsdom or unsupported */
          }
        }
      },
      [disabled, motionX],
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent<HTMLButtonElement>) => {
        if (!pointerStart.current) return;
        const delta = e.clientX - pointerStart.current.clientX;

        if (!dragging.current) {
          if (Math.abs(delta) < DRAG_DEAD_ZONE) return;
          dragging.current = true;
        }

        const dragMin = THUMB_OFFSET;
        const pressedThumbWidth = THUMB_SIZE + PRESS_EXTEND;
        const dragMax = TRACK_WIDTH - THUMB_OFFSET - pressedThumbWidth;
        const rawX = pointerStart.current.originX + delta;
        motionX.set(Math.max(dragMin, Math.min(dragMax, rawX)));
      },
      [motionX],
    );

    const handlePointerUp = useCallback(() => {
      if (!pointerStart.current) return;
      setPressed(false);

      if (dragging.current) {
        didDrag.current = true;
        dragging.current = false;

        const currentX = motionX.get();
        const dragMin = THUMB_OFFSET;
        const pressedThumbWidth = THUMB_SIZE + PRESS_EXTEND;
        const dragMax = TRACK_WIDTH - THUMB_OFFSET - pressedThumbWidth;
        const midpoint = (dragMin + dragMax) / 2;

        const shouldBeOn = currentX > midpoint;

        if (shouldBeOn !== currentChecked) {
          emitChange(shouldBeOn);
        } else {
          const snapTarget = currentChecked ? THUMB_OFFSET + THUMB_TRAVEL : THUMB_OFFSET;
          animate(motionX, snapTarget, springs.moderate);
        }

        requestAnimationFrame(() => {
          didDrag.current = false;
        });
      }

      pointerStart.current = null;
    }, [currentChecked, emitChange, motionX]);

    const handleCheckedChange = useCallback(
      (next: boolean) => {
        if (didDrag.current) return;
        emitChange(next);
      },
      [emitChange],
    );

    return (
      <SwitchPrimitives.Root
        ref={ref}
        checked={currentChecked}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={(e) => {
          if (didDrag.current) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
        className={cn(
          'relative shrink-0 rounded-full outline-none cursor-pointer touch-none select-none',
          'transition-colors duration-75',
          'focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        style={{
          width: TRACK_WIDTH,
          height: TRACK_HEIGHT,
          backgroundColor: currentChecked
            ? hovered
              ? 'color-mix(in oklab, var(--foreground) 88%, transparent)'
              : 'var(--foreground)'
            : hovered
              ? 'color-mix(in oklab, var(--input), var(--foreground) 10%)'
              : 'var(--input)',
        }}
        {...props}
      >
        <SwitchPrimitives.Thumb asChild>
          <motion.span
            className="absolute top-0 left-0 block rounded-full bg-background shadow-sm"
            initial={false}
            style={{ x: motionX }}
            animate={{
              y: thumbY,
              width: thumbWidth,
              height: thumbHeight,
            }}
            transition={hasMounted.current ? springs.moderate : { duration: 0 }}
          />
        </SwitchPrimitives.Thumb>
      </SwitchPrimitives.Root>
    );
  },
);

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
